import React, { useState, useEffect, useMemo } from "react";
import StoreSidebar from "../components/StoreSidebar";
import { db } from "../firebase/firebase";

import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Users, Search, Eye, ShoppingBag, 
  Activity, X, Phone, User as UserIcon, Navigation, Calendar, FileText, Printer
} from "lucide-react";

export default function Customers() {
  const [activeBranchId, setActiveBranchId] = useState("");
  const [activeStoreName, setActiveStoreName] = useState("Loading Store...");
  
  const [activeStoreAdress, setActiveStoreAdress] = useState("Loading Address...");
    const [activeStoreNumber, setActiveStoreNumber] = useState("Loading Landline...");

  
  // Data Collections
  const [customers, setCustomers] = useState([]);
  const [sales, setSales] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal / Audit States
  const [viewingCustomer, setViewingCustomer] = useState(null);
  const [auditTab, setAuditTab] = useState("profile");

  // --- 1. DETECT USER UID & FETCH MATCHING STORE ---
  // (Updated to match your StoreSidebar logic using "linkedAuthUid")
  useEffect(() => {
    const auth = getAuth();
    let unsubStores = () => {};

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (user) {
        // Query stores collection where linkedAuthUid matches the user's uid
        const storeQuery = query(
          collection(db, "stores"), 
          where("linkedAuthUid", "==", user.uid)
        );
        
        unsubStores = onSnapshot(storeQuery, (snapshot) => {
          if (!snapshot.empty) {
            const storeDoc = snapshot.docs[0]; 
            const data = storeDoc.data();
            
            // activeBranchId must be the Firestore Document ID so the subcollection paths work
            setActiveBranchId(storeDoc.id);
            setActiveStoreName(data.storeId || "Unnamed Store");
            setActiveStoreAdress(data.address || "CHANDRAKANTI COMPLEX OPP KHAJPURA, PATNA - 800014, Bihar, India" );
            setActiveStoreNumber(data.phone || "06123590450");
          } else {
            console.warn("No matching store found for this user UID.");
            setActiveBranchId("");
            setActiveStoreName("No Assigned Store");
          }
        });
      } else {
        setActiveBranchId("");
        setActiveStoreName("Please Log In");
      }
    });

    return () => {
      unsubAuth();
      unsubStores();
    };
  }, []);

  // --- 2. FETCH STORE SPECIFIC DATA ---
  useEffect(() => {
    if (!activeBranchId) return;

    const unsubCust = onSnapshot(collection(db, "stores", activeBranchId, "customers"), (snap) => {
      setCustomers(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubSales = onSnapshot(collection(db, "stores", activeBranchId, "sales"), (snap) => {
      setSales(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)));
    });

    const unsubPresc = onSnapshot(collection(db, "stores", activeBranchId, "prescriptions"), (snap) => {
      setPrescriptions(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })).sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp)));
    });

    return () => { unsubCust(); unsubSales(); unsubPresc(); };
  }, [activeBranchId]);

  // --- COMPUTE CUSTOMER LEDGER ---
  const consolidatedCustomers = useMemo(() => {
    const uniqueMap = new Map();
    customers.forEach(c => {
      const key = c.phone || c.id;
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, { ...c, orderCount: 1 });
      } else {
        const existing = uniqueMap.get(key);
        existing.orderCount += 1;
      }
    });

    const list = Array.from(uniqueMap.values());
    
    const listWithRecords = list.map(c => {
      return {
        ...c,
        theirOrders: sales.filter(s => s.customer?.phone === c.phone),
        theirPrescriptions: prescriptions.filter(p => p.phone === c.phone || p.patient?.mobile === c.phone)
      }
    });

    if (!searchQuery) return listWithRecords;
    return listWithRecords.filter(c => 
      c.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
      c.phone?.includes(searchQuery)
    );
  }, [customers, sales, prescriptions, searchQuery]);

  // --- OPEN AUDIT MODAL ---
  const handleOpenAudit = (customer) => {
    setViewingCustomer(customer);
    setAuditTab("profile");
  };

  // --- PRINT FULL AUDIT DATA FORMAT ---
  const handlePrintFullAudit = (customer) => {
    const dateStr = new Date().toLocaleDateString();

    let ordersHtml = '';
    if (!customer.theirOrders || customer.theirOrders.length === 0) {
      ordersHtml = `<div style="padding: 15px; text-align: center; color: #666; font-size: 13px;">No sales history logged for this client yet.</div>`;
    } else {
      ordersHtml = customer.theirOrders.map(order => `
        <div style="padding: 15px; border-bottom: 1px dashed #ddd;">
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px;">
            <div><strong>Invoice:</strong> #${order.invoiceNo}</div>
            <div><strong>Date:</strong> ${order.timestamp?.split("T")[0] || '-'}</div>
          </div>
          <table style="margin-bottom: 10px;">
            <tr>
              <th style="text-align: left;">Item Description</th>
              <th>Qty</th>
              <th style="text-align: right;">Total</th>
            </tr>
            ${order.items?.map(item => `
              <tr>
                <td style="text-align: left; font-weight: 500;">${item.productName}</td>
                <td>${item.quantity}</td>
                <td style="text-align: right; font-weight: bold;">₹${(item.retailPrice * item.quantity).toLocaleString()}</td>
              </tr>
            `).join('')}
          </table>
          <div style="display: flex; justify-content: space-between; font-size: 13px;">
            <span style="color: ${order.orderStatus === 'Pending' ? '#d97706' : '#059669'}; font-weight: bold; text-transform: uppercase;">${order.orderStatus}</span>
            <strong style="font-size: 15px; color: #16205f;">Net Paid: ₹${order.financials?.netPayable?.toLocaleString() || '0'}</strong>
          </div>
        </div>
      `).join('');
    }

    let prescriptionsHtml = '';
    if (!customer.theirPrescriptions || customer.theirPrescriptions.length === 0) {
      prescriptionsHtml = `<div style="padding: 15px; text-align: center; color: #666; font-size: 13px;">No clinical measurements recorded for this client.</div>`;
    } else {
      prescriptionsHtml = customer.theirPrescriptions.map((record, index) => `
        <div style="padding: 15px; border-bottom: ${index === customer.theirPrescriptions.length - 1 ? 'none' : '1px dashed #ddd'};">
          <div style="display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px;">
            <div><strong>Recorded On:</strong> ${record.timestamp ? record.timestamp.split("T")[0] : "-"}</div>
            <div><strong>Optometrist:</strong> ${record.doctorName || activeStoreName}</div>
          </div>
          <table>
            <tr>
              <th rowspan="2" style="background:#eef2f6;">EYE</th>
              <th colspan="4">DISTANCE VISION (DV)</th>
              <th colspan="3">NEAR VISION (NV)</th>
              <th rowspan="2" style="background:#eef2f6;">ADD</th>
            </tr>
            <tr>
              <th>SPH</th><th>CYL</th><th>AXIS</th><th>VA</th>
              <th>SPH</th><th>CYL</th><th>AXIS</th>
            </tr>
            <tr>
              <td style="background:#eef2f6;">RIGHT (OD)</td>
              <td>${record.sphRight || '-'}</td><td>${record.cylRight || '-'}</td><td>${record.axisRight || '-'}</td><td>${record.vaRight || '-'}</td>
              <td>${record.nvSphRight || '-'}</td><td>${record.nvCylRight || '-'}</td><td>${record.nvAxisRight || '-'}</td>
              <td style="color:#b40000;">${record.addRight || '-'}</td>
            </tr>
            <tr>
              <td style="background:#eef2f6;">LEFT (OS)</td>
              <td>${record.sphLeft || '-'}</td><td>${record.cylLeft || '-'}</td><td>${record.axisLeft || '-'}</td><td>${record.vaLeft || '-'}</td>
              <td>${record.nvSphLeft || '-'}</td><td>${record.nvCylLeft || '-'}</td><td>${record.nvAxisLeft || '-'}</td>
              <td style="color:#b40000;">${record.addLeft || '-'}</td>
            </tr>
          </table>
          ${(record.clSphRight || record.clSphLeft) ? `
          <table style="margin-top: 10px;">
            <tr><th colspan="4" style="background:#eef2f6; text-align: left;">Contact Lens Specifications</th></tr>
            <tr><th style="background:#fafafa;">EYE</th><th>SPH</th><th>CYL</th><th>AXIS</th></tr>
            <tr><td style="background:#fafafa;">RIGHT (OD)</td><td>${record.clSphRight || '-'}</td><td>${record.clCylRight || '-'}</td><td>${record.clAxisRight || '-'}</td></tr>
            <tr><td style="background:#fafafa;">LEFT (OS)</td><td>${record.clSphLeft || '-'}</td><td>${record.clCylLeft || '-'}</td><td>${record.clAxisLeft || '-'}</td></tr>
          </table>` : ''}
          ${record.prescriptionNotes ? `<div style="margin-top: 10px; font-size: 12px; color: #444;"><strong>Clinical Notes:</strong> ${record.prescriptionNotes}</div>` : ''}
        </div>
      `).join('');
    }

    const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Full Audit Report - ${customer.name || 'Client'}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; font-family: 'Segoe UI', Arial, sans-serif; }
      @page { size: A4; margin: 15mm; padding: 10mm; }
      body { background: #fff; color: #222; -webkit-print-color-adjust: exact; }
      .a4-page { width: 100%; margin: 0 auto; background: white; }
      .header {
  position: relative;
  text-align: center;
  border-bottom: 2px solid #16205f;
  padding-bottom: 15px;
  margin-bottom: 20px;
  min-height: 90px;
}

.logo {
  position: absolute;
  left: 0;
  top: 0;
}

.logo img {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.header-content {
  display: inline-block;
  text-align: center;
}

.clinic-name {
  font-size: 26px;
  font-weight: 900;
  color: #16205f;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.store-address {
  font-size: 12px;
  color: #555;
  margin-bottom: 8px;
}

.tagline {
  color: #555;
  font-size: 12px;
  margin-bottom: 10px;
}

.title {
  display: inline-block;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  background: #16205f;
  padding: 6px 20px;
  margin-bottom: 10px;
  border-radius: 20px;
  text-transform: uppercase;
}
      .patient-section { display: flex; justify-content: space-between; margin-bottom: 25px; gap: 20px; }
      .info-box { border: 1px solid #e4e4e4; border-radius: 8px; padding: 15px; flex: 1; background: #fafafa; }
      .info-box h4 { font-size: 11px; color: #666; text-transform: uppercase; margin-bottom: 8px; border-bottom: 1px solid #ddd; padding-bottom: 4px; }
      .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 13px; }
      .info-row strong { color: #111; }
      
      .section { border: 1px solid #ddd; border-radius: 8px; margin-bottom: 25px; overflow: hidden; }
      .section-title { background: #f0f4f8; padding: 10px 15px; font-size: 14px; font-weight: bold; border-bottom: 1px solid #ddd; color: #16205f; text-transform: uppercase; }
      
      table { width: 100%; border-collapse: collapse; }
      th { background: #fafafa; font-size: 11px; padding: 10px; border: 1px solid #ddd; text-transform: uppercase; color: #444; }
      td { text-align: center; padding: 10px; border: 1px solid #ddd; font-size: 13px; font-weight: 600; color: #111; }
      
      .footer { text-align: center; margin-top: 40px; color: #888; font-size: 10px; border-top: 1px solid #eee; padding-top: 10px; }
    </style>
    </head>
    <body>
      <div class="a4-page">

          <div class="header">
  <div class="logo">
    <img src="${window.location.origin}/logo192.png" alt="Logo" />
  </div>

  <div class="header-content">
    <div class="clinic-name">Shruti Eye Care & Opticals</div>

    <div class="store-address">  ${activeStoreAdress}</br>
      Mobile No: 9470835831, 9939605949 | Landline: ${activeStoreNumber}
    </div>

    <div class="tagline">
      Complete Eye Care • Computerized Eye Testing • Contact Lens • Premium Frames
    </div>

    <div class="title">Complete Client Audit Report</div>
  </div>

        <div class="patient-section">
          <div class="info-box">
            <h4>Identity & Demographics</h4>
            <div class="info-row"><span>Name:</span> <strong>${customer.name || 'Walk-in Patient'}</strong></div>
            <div class="info-row"><span>Mobile:</span> <strong>${customer.phone || 'N/A'}</strong></div>
            <div class="info-row"><span>Age / Gender:</span> <strong>${customer.age ? customer.age + ' Yrs' : '-'} / ${customer.gender || '-'}</strong></div>
            <div class="info-row"><span>DOB:</span> <strong>${customer.dob || '-'}</strong></div>
          </div>
          <div class="info-box">
            <h4>Routing & Location</h4>
            <div class="info-row"><span>Audit Date:</span> <strong>${dateStr}</strong></div>
            <div class="info-row"><span>Address:</span> <strong>${customer.address || '-'}</strong></div>
            <div class="info-row"><span>State / Pincode:</span> <strong>${customer.state || '-'} / ${customer.zipcode || customer.pincode || '-'}</strong></div>
          </div>
        </div>

        <div class="section">
          <div class="section-title">Billing Ledger & Order History</div>
          ${ordersHtml}
        </div>

        <div class="section">
          <div class="section-title">Clinical Data & Prescriptions</div>
          ${prescriptionsHtml}
        </div>

        <div class="footer">
          © ${activeStoreName} | Generated via Store Management System
        </div>
      </div>
      <script>
        window.addEventListener('load', function() {
          setTimeout(function() {
            window.print();
            setTimeout(function() { window.close(); }, 500);
          }, 300);
        });
      </script>
    </body>
    </html>
    `;

    const printWindow = window.open('', '_blank', 'width=800,height=900');
    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } else {
      alert("Please allow popups to print reports.");
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50 text-slate-800 font-sans text-base">
      <StoreSidebar />

      <div className="flex-1 p-6 lg:p-10 space-y-6 overflow-x-hidden">
        
        {/* HEADER CONTROLS */}
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-slate-800 uppercase tracking-wider flex items-center gap-3">
              <Users size={32} className="text-blue-600" /> Customer Matrix
            </h1>
            <p className="text-slate-500 mt-1.5 text-sm font-medium">Manage relationships, view order history, and access clinical profiles.</p>
          </div>

          <div className="flex flex-col gap-3 w-full md:w-auto">
            {/* Displaying assigned store instead of a dropdown */}
            <div className="bg-blue-50 p-3 rounded-xl border border-blue-200 text-blue-700 font-bold text-sm w-full md:w-64 flex items-center justify-center text-center">
              {activeStoreName}
            </div>
          </div>
        </div>

        {/* SEARCH BAR */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center gap-3">
          <div className="flex flex-1 items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2 focus-within:border-blue-500 focus-within:bg-white transition-colors">
            <Search className="text-blue-500" size={20} />
            <input 
              type="text" 
              placeholder="Search by customer name or registered phone number..." 
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="bg-transparent border-none outline-none w-full text-sm py-1.5 text-slate-800 placeholder:text-slate-400 font-medium" 
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="text-slate-400 hover:text-slate-600">
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* CUSTOMER DIRECTORY TABLE */}
        <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left whitespace-nowrap">
              <thead className="bg-slate-50">
                <tr className="border-b border-slate-200 text-slate-500 text-xs uppercase tracking-wider font-bold">
                  <th className="p-5 pl-6">Client Profile Name</th>
                  <th className="p-5">Registered Mobile No.</th>
                  <th className="p-5 text-center">Total Lifetime Orders</th>
                  <th className="p-5 pr-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 font-medium">
                {consolidatedCustomers.length === 0 ? (
                  <tr><td colSpan="4" className="p-10 text-center text-slate-500 italic text-sm">No customer records located matching your search.</td></tr>
                ) : (
                  consolidatedCustomers.map((c, i) => (
                    <tr key={i} className="hover:bg-slate-50/50 transition-colors">
                      <td className="p-5 pl-6 text-slate-800 font-black text-lg">{c.name || "Unknown Client"}</td>
                      <td className="p-5 font-mono text-slate-600 font-bold">{c.phone || "No Phone"}</td>
                      <td className="p-5 text-center">
                        <span className="bg-emerald-50 border border-emerald-200 text-emerald-600 px-3 py-1 rounded-lg font-black text-sm">
                          {c.orderCount}
                        </span>
                      </td>
                      <td className="p-5 pr-6 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={() => handleOpenAudit(c)} 
                            className="px-4 py-2 bg-slate-50 hover:bg-slate-800 text-slate-600 hover:text-white border border-slate-200 rounded-xl transition-all font-bold text-xs uppercase tracking-wider inline-flex items-center gap-2"
                          >
                            <Eye size={14} /> Audit
                          </button>
                          <button 
                            onClick={() => handlePrintFullAudit(c)} 
                            className="px-4 py-2 bg-blue-50 hover:bg-blue-600 text-blue-600 hover:text-white border border-blue-200 rounded-xl transition-all font-bold text-xs uppercase tracking-wider inline-flex items-center gap-2"
                          >
                            <Printer size={14} /> Print Audit
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* FULL-SCREEN AUDIT MODAL OVERLAY */}
      <AnimatePresence>
        {viewingCustomer && (
          <div className="fixed inset-0 z-50 flex justify-end bg-slate-900/40 backdrop-blur-sm">
            <motion.div 
              initial={{ x: "100%" }} 
              animate={{ x: 0 }} 
              exit={{ x: "100%" }} 
              transition={{ type: "spring", stiffness: 100, damping: 20 }}
              className="w-full md:w-[650px] lg:w-[800px] h-full bg-white shadow-2xl border-l border-slate-200 flex flex-col"
            >
              {/* Modal Header */}
              <div className="p-6 lg:p-8 border-b border-slate-200 flex justify-between items-center bg-slate-50">
                <div>
                  <h2 className="text-2xl font-black text-slate-800">{viewingCustomer.name?.toUpperCase() || "CLIENT"}</h2>
                  <p className="text-blue-600 font-mono mt-1 text-sm font-bold flex items-center gap-2"><Phone size={16}/> {viewingCustomer.phone}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => handlePrintFullAudit(viewingCustomer)}
                    className="p-3 bg-blue-600 text-white hover:bg-blue-700 rounded-2xl transition-colors shadow-sm flex items-center gap-2 text-sm font-bold px-4"
                  >
                    <Printer size={18} /> Print Full Audit
                  </button>
                  <button onClick={() => setViewingCustomer(null)} className="p-3 bg-white border border-slate-200 hover:bg-rose-50 hover:border-rose-200 hover:text-rose-600 rounded-2xl transition-colors text-slate-500 shadow-sm">
                    <X size={20} />
                  </button>
                </div>
              </div>

              {/* Modal Tabs */}
              <div className="flex bg-slate-50 px-6 lg:px-8 pt-2 gap-2 border-b border-slate-200">
                <button onClick={() => setAuditTab("profile")} className={`px-6 py-3 text-center font-bold text-xs uppercase tracking-wider rounded-t-xl transition-colors border-t border-l border-r border-transparent ${auditTab === "profile" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:text-slate-800"}`}>
                  <UserIcon size={14} className="inline mr-1.5 mb-0.5" /> Personal Bio
                </button>
                <button onClick={() => setAuditTab("orders")} className={`px-6 py-3 text-center font-bold text-xs uppercase tracking-wider rounded-t-xl transition-colors border-t border-l border-r border-transparent ${auditTab === "orders" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:text-slate-800"}`}>
                  <ShoppingBag size={14} className="inline mr-1.5 mb-0.5" /> Billing Ledger
                </button>
                <button onClick={() => setAuditTab("prescriptions")} className={`px-6 py-3 text-center font-bold text-xs uppercase tracking-wider rounded-t-xl transition-colors border-t border-l border-r border-transparent ${auditTab === "prescriptions" ? "bg-white text-blue-600 border-slate-200 !border-b-white translate-y-[1px]" : "text-slate-500 hover:text-slate-800"}`}>
                  <Activity size={14} className="inline mr-1.5 mb-0.5" /> Clinical Data
                </button>
              </div>

              {/* Modal Content Scroll Area */}
              <div className="flex-1 overflow-y-auto p-6 lg:p-8 bg-white space-y-8">
                
                {/* PROFILE SEGMENT */}
                {auditTab === 'profile' && (
                  <div className="space-y-6">
                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm">
                      <h3 className="text-xs text-blue-600 font-black uppercase tracking-widest border-b border-slate-200 pb-3 mb-4 flex items-center gap-2"><UserIcon size={16}/> Identity & Demographics</h3>
                      <div className="grid grid-cols-2 gap-y-4 gap-x-6">
                        <div><span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">Full Name</span><span className="font-bold text-slate-800 text-sm">{viewingCustomer.name || "-"}</span></div>
                        <div><span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">Mobile Contact</span><span className="font-mono font-bold text-slate-800 text-sm">{viewingCustomer.phone || "-"}</span></div>
                        <div><span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">Date of Birth (DOB)</span><span className="font-bold text-slate-800 text-sm">{viewingCustomer.dob || "-"}</span></div>
                        <div><span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">Current Age</span><span className="font-bold text-slate-800 text-sm">{viewingCustomer.age ? `${viewingCustomer.age} Years` : "-"}</span></div>
                        <div><span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">Gender</span><span className="font-bold text-slate-800 text-sm">{viewingCustomer.gender || "-"}</span></div>
                      </div>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm">
                      <h3 className="text-xs text-emerald-600 font-black uppercase tracking-widest border-b border-slate-200 pb-3 mb-4 flex items-center gap-2"><Navigation size={16}/> Routing & Location</h3>
                      <div className="space-y-4">
                        <div>
                          <span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">Full Street Address</span>
                          <span className="font-bold text-slate-800 text-sm block bg-white p-3 rounded-xl border border-slate-200 shadow-inner">
                            {viewingCustomer.address || "Address not provided in registry."}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-6">
                          <div><span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">State / Region</span><span className="font-bold text-slate-800 text-sm">{viewingCustomer.state || "-"}</span></div>
                          <div><span className="block text-[10px] uppercase text-slate-400 font-bold mb-1">Zipcode / PIN</span><span className="font-mono font-bold text-slate-800 text-sm">{viewingCustomer.zipcode || viewingCustomer.pincode || "-"}</span></div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ORDERS SEGMENT */}
                {auditTab === 'orders' && (
                  <div className="space-y-5">
                    {viewingCustomer.theirOrders?.length === 0 ? (
                      <div className="text-center text-slate-400 font-medium p-10 bg-slate-50 border border-slate-200 rounded-2xl">
                        <ShoppingBag size={32} className="mx-auto text-slate-300 mb-3" />
                        No sales history logged for this client yet.
                      </div>
                    ) : (
                      viewingCustomer.theirOrders.map(order => (
                        <div key={order.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm hover:shadow-md transition-shadow">
                          <div className="flex flex-wrap justify-between items-start border-b border-slate-100 pb-4 mb-4 gap-4">
                            <div>
                              <span className="text-[10px] uppercase text-slate-400 font-bold block mb-0.5">Invoice Code</span>
                              <span className="text-lg font-black text-blue-600 font-mono">#{order.invoiceNo}</span>
                            </div>
                            <div className="text-right">
                              <span className="text-[10px] uppercase text-slate-400 font-bold block mb-0.5">Transaction Date</span>
                              <span className="font-mono text-slate-800 font-bold">{order.timestamp?.split("T")[0]}</span>
                            </div>
                          </div>

                          <div className="bg-slate-50 p-4 rounded-xl border border-slate-200 mb-4">
                            <span className="text-[10px] text-slate-500 uppercase tracking-widest font-black block mb-2 flex items-center gap-1.5"><FileText size={12}/> Order Line Items</span>
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm py-1.5 border-b border-slate-200/60 last:border-0">
                                <span className="text-slate-800 font-bold">{item.productName} <span className="text-slate-400 font-normal">x{item.quantity}</span></span>
                                <span className="font-mono font-bold text-slate-600">₹{(item.retailPrice * item.quantity).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>

                          <div className="flex justify-between items-end">
                            <span className={`px-3 py-1 rounded-md text-[10px] font-black uppercase tracking-widest ${order.orderStatus === 'Pending' ? 'bg-amber-50 text-amber-600 border border-amber-200' : 'bg-emerald-50 text-emerald-600 border border-emerald-200'}`}>
                              {order.orderStatus}
                            </span>
                            <div className="text-right">
                              <span className="text-[10px] uppercase text-slate-400 block font-bold mb-0.5">Net Total</span>
                              <span className="text-xl font-black text-slate-800 font-mono">₹{order.financials?.netPayable?.toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* PRESCRIPTIONS SEGMENT */}
                {auditTab === 'prescriptions' && (
                  <div className="space-y-5">
                    {viewingCustomer.theirPrescriptions?.length === 0 ? (
                      <div className="text-center text-slate-400 font-medium p-10 bg-slate-50 border border-slate-200 rounded-2xl">
                        <Activity size={32} className="mx-auto text-slate-300 mb-3" />
                        No clinical measurements recorded.
                      </div>
                    ) : (
                      viewingCustomer.theirPrescriptions.map(rx => (
                        <div key={rx.id} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4 hover:shadow-md transition-shadow">
                          <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-3">
                            <span className="font-black text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><Calendar size={14}/> Recorded On</span>
                            <span className="font-mono text-slate-800 font-bold">{rx.timestamp ? rx.timestamp.split("T")[0] : "-"}</span>
                          </div>

                          {/* Simplified Rx Preview */}
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                              <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest block mb-2">Right Eye (OD)</span>
                              <div className="text-sm font-mono text-slate-800 font-bold flex flex-col gap-1">
                                <div className="flex justify-between border-b border-slate-200/50 pb-1">
                                  <span className="text-slate-400">SPH</span> <span className="text-slate-800">{rx.sphRight || "-"}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">CYL</span> <span className="text-slate-800">{rx.cylRight || "-"}</span>
                                </div>
                              </div>
                            </div>
                            <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                              <span className="text-[10px] font-black text-purple-600 uppercase tracking-widest block mb-2">Left Eye (OS)</span>
                              <div className="text-sm font-mono text-slate-800 font-bold flex flex-col gap-1">
                                <div className="flex justify-between border-b border-slate-200/50 pb-1">
                                  <span className="text-slate-400">SPH</span> <span className="text-slate-800">{rx.sphLeft || "-"}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">CYL</span> <span className="text-slate-800">{rx.cylLeft || "-"}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {rx.prescriptionNotes && (
                            <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 text-sm">
                              <span className="text-[10px] uppercase font-black text-blue-500 block mb-1">Optometrist Notes</span>
                              <span className="text-blue-900 font-medium">{rx.prescriptionNotes}</span>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
                
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
}